<template>
  <div
    class="fp-step"
    :class="{
      'fp-step-reset-password': !isSuccess,
      'fp-step-reset-password-success': isSuccess
    }"
  >
    <template v-if="!isSuccess">
      <div class="fp-step-header-reset">
        <button type="button" class="fp-back-btn" @click="$emit('go-back')" t_label="btn_emp2_Back_ResetPassword">
          <img 
            src="https://cdn.adsalecdn.com/EMP/2-0/BuyerIdx/All_Categories.svg" 
            alt="back" 
            class="fp-back-icon"
          />
        </button>
        <h1 class="fp-step-title">{{ title || $t('lbl_emp2_ResetPassword') }}</h1>
      </div>

    <!-- Password inputs -->
    <DuplicatePassword
      :new-password="localNewPassword"
      :confirm-password="localConfirmPassword"
      @update:newPassword="handleNewPasswordUpdate"
      @update:confirmPassword="handleConfirmPasswordUpdate"
    />

    <!-- Reset password button -->
    <button
      class="fp-btn-primary"
      @click="handleSubmit"
      :disabled="isResetDisabled"
      t_label="btn_emp2_ResetPassword_Submit"
    >
      {{ $t('lbl_emp2_ResetPassword') }}
    </button>
    <div v-if="globalError" class="fp-error-message fp-global-error">
      {{ globalError }}
    </div>
    </template>

    <template v-else>
      <h1 class="fp-step-title">{{ successTitle }}</h1>
      <div class="fp-form-group fp-form-group-success">
        <div class="fp-info-text">
          {{ successDescription }}
        </div>
      </div>
      <button class="fp-btn-primary" @click="$emit('go-to-login')" :disabled="loading" t_label="btn_emp2_BackToLogin_ResetPasswordSuccess">
        {{ successButtonText }}
      </button>
    </template>
  </div>
</template>

<script>
import DuplicatePassword from '../../UserSetting/DuplicatePassword.vue';

export default {
  name: 'ResetPassword',
  components: {
    DuplicatePassword
  },
  emits: [
    'go-back',
    'reset-password',
    'go-to-login',
    'update:newPassword',
    'update:confirmPassword'
  ],
  props: {
    /**
     * 初始新密码（可选，用于外部同步状态）
     */
    newPassword: {
      type: String,
      default: ''
    },
    /**
     * 初始确认密码（可选，用于外部同步状态）
     */
    confirmPassword: {
      type: String,
      default: ''
    },
    /**
     * 按钮 loading 状态
     */
    loading: {
      type: Boolean,
      default: false
    },
    /**
     * 标题文案，默认使用翻译键 lbl_emp2_ResetPassword
     */
    title: {
      type: String,
      default: ''
    },
    /**
     * 是否展示成功态（true 时仅显示成功提示）
     */
    isSuccess: {
      type: Boolean,
      default: false
    },
    successTitle: {
      type: String,
      default: 'Password reset successful'
    },
    successDescription: {
      type: String,
      default: 'Your account has successfully reset to new password.'
    },
    successButtonText: {
      type: String,
      default: 'Back to Login'
    }
  },
  data() {
    return {
      localNewPassword: this.newPassword,
      localConfirmPassword: this.confirmPassword,
      globalError: ''
    };
  },
  computed: {
    isConfirmMismatch() {
      return this.localConfirmPassword && this.localNewPassword !== this.localConfirmPassword;
    },
    isResetDisabled() {
      return (
        !this.localNewPassword ||
        !this.localConfirmPassword ||
        this.isConfirmMismatch ||
        this.localNewPassword.length < 6 ||
        this.loading
      );
    }
  },
  watch: {
    newPassword(val) {
      this.localNewPassword = val;
    },
    confirmPassword(val) {
      this.localConfirmPassword = val;
    }
  },
  methods: {
    handleNewPasswordUpdate(value) {
      this.localNewPassword = value;
      this.globalError = '';
      this.$emit('update:newPassword', value);
    },
    handleConfirmPasswordUpdate(value) {
      this.localConfirmPassword = value;
      this.globalError = '';
      this.$emit('update:confirmPassword', value);
    },
    handleSubmit() {
      this.globalError = '';
      if (!this.localNewPassword) {
        this.globalError = 'Please enter a new password';
        return;
      }
      if (!this.localConfirmPassword) {
        this.globalError = 'Please confirm your password';
        return;
      }
      if (this.isConfirmMismatch) {
        this.globalError = 'Passwords do not match';
        return;
      }
      if (this.localNewPassword.length < 6) {
        this.globalError = this.$t('EMP_UserSetting_PasswordStrengthTip');
        return;
      }
      
      this.$emit('reset-password', {
        newPassword: this.localNewPassword,
        confirmPassword: this.localConfirmPassword
      });
    }
  }
};
</script>

<style scoped>
.fp-step {
  display: flex;
  flex-direction: column;
  width: 100%;
  align-self: stretch;
  padding: 0;
  margin: 0;
  gap: 24px;
}

.fp-step-reset-password {
  gap: 24px;
}

.fp-step-header-reset {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
}

.fp-back-btn {
  width: 20px;
  height: 20px;
  padding: 0;
  background: transparent;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.fp-back-btn .fp-back-icon {
  width: 20px;
  height: 20px;
  transform: scaleX(-1);
}

.fp-step-title {
  color: rgba(0, 0, 0, 0.80);
  font-size: 28px;
  font-family: 'Inter', sans-serif;
  font-weight: 700;
  line-height: 36.40px;
  margin: 0;
  word-wrap: break-word;
  flex: 1 1 0;
}

.fp-form-group {
  align-self: stretch;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 16px;
  display: flex;
}

.fp-error-message {
  align-self: stretch;
  padding-left: 14px;
  padding-right: 14px;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  display: flex;
  color: #D32F2F;
  font-size: 12px;
  font-family: 'Roboto', sans-serif;
  font-weight: 400;
  line-height: 20px;
  letter-spacing: 0.40px;
  word-wrap: break-word;
}

.fp-btn-primary {
  align-self: stretch;
  height: 42px;
  padding: 0 24px;
  background: #7366FF;
  color: white;
  border: none;
  border-radius: 999px;
  font-size: 14px;
  font-family: 'Inter', sans-serif;
  font-weight: 600;
  line-height: 21px;
  cursor: pointer;
  transition: all 0.2s;
  display: inline-flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  box-sizing: border-box;
}

.fp-btn-primary:hover:not(:disabled) {
  background: #5a4dcc;
  color: white;
  border: none;
}

.fp-btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  background: rgba(0, 18, 26, 0.38);
  color: rgba(255, 255, 255, 0.9);
}

.fp-global-error {
  margin-top: -8px;
}

.fp-step-reset-password-success {
  gap: 24px !important;
}

.fp-form-group-success {
  margin: 0;
  padding: 0;
  gap: 0;
}

.fp-info-text {
  color: rgba(0, 0, 0, 0.60);
  font-size: 14px;
  font-family: 'Inter', sans-serif;
  font-weight: 400;
  line-height: 18.20px;
  word-wrap: break-word;
  text-align: left;
}
</style>
